import { CacheConfig, type CacheStatus, getCachedItem, setCacheItem, setCacheStatus } from '@r-and-a-shared-ui/core-caching';
import { IContentItem } from '@kontent-ai/delivery-sdk';
import { deliveryClient } from '../config/kontent-delivery-client';
import { adjustSystemProperties } from '../utils';
import { ITEM_WITH_LINKED_ITEMS_CACHE_KEY_PREFIX } from '../constants/cacheKeyPrefixes';

const inFlightFetches = new Map<string, Promise<IContentItem>>();

export const getItemWithLinkedItemsByCodename = async <
  Type extends IContentItem,
>({
  codename = '',
  depthParameter = 3,
  cache = true,
  cacheTTL = CacheConfig.TTL.PageElement.valueOf()
}: {
  codename: string;
  depthParameter?: number;
  cache?: boolean;
  cacheTTL?: number;
},
  cacheStatus?: CacheStatus): Promise<Type | undefined> => {

  const cacheKey = `${ITEM_WITH_LINKED_ITEMS_CACHE_KEY_PREFIX}${codename}${
    depthParameter ? `-depth-${depthParameter}` : ''
  }`;

  try {
    const cachedItem = getCachedItem<Type>(cacheKey, cache);

    if (cachedItem) {
      setCacheStatus(cacheStatus, {
        cacheable: cache,
        xInMemoryCache: CacheConfig.Hit,
        cacheTTL
      });
      return cachedItem;
    }

    // Fetch is already in progress for this codename
    if (inFlightFetches.has(cacheKey)) {
      return inFlightFetches.get(cacheKey)! as Promise<Type>;
    }

    const fetchPromise = (async (): Promise<Type> => {
      try {
        const response = await deliveryClient
          .item<Type>(codename)
          .depthParameter(depthParameter)
          .toPromise();

        adjustSystemProperties(response?.data?.item);

        const result = response?.data?.item as Type;

        setCacheItem<Type>(cacheKey, result, cacheTTL, cache);

        setCacheStatus(cacheStatus, {
          cacheable: cache,
          xInMemoryCache: CacheConfig.Miss,
          cacheTTL
        });
        return result;
      }
      catch (error: any) {
        console.error('Error fetching getItemWithLinkedItemsByCodename with linked items by codename:', error);
        if (error?.errors?.length) {
          console.error('Aggregate errors from getItemWithLinkedItemsByCodename:\n');
          error.errors.map((e: any) => console.error(e));
        }
        throw error;
      }
      finally {
        inFlightFetches.delete(cacheKey);
      }
    })();

    inFlightFetches.set(cacheKey, fetchPromise as Promise<IContentItem>);

    return fetchPromise as Promise<Type>;
  } catch (error) {
    console.error('Error in getItemWithLinkedItemsByCodename', error);
    throw error;
  }
};
